import axios from "../utils/index";
import type { AcceptCriterion } from "../utils/acceptCriteriaUtils";
import { formatAcceptCriterionRate } from "../utils/acceptCriteriaUtils";

export interface ShiftTemplateRequest {
  templateName: string;
  specialty?: string | null;
  skillLevel?: string | null;
  supportLevel?: string | null;
  additionalFilesRequired?: string[];
  travelFlag?: number;
  travelType?: string | null;
  travelDetails?: string;
  fightsIncludedFlag?: number;
  accommodationFlag?: number;
  accommodationType?: string | null;
  accommodation?: string;
  startTime?: string;
  endTime?: string;
  description?: string;
  positionsRequired?: number;
  acceptCriteria?: AcceptCriterion[];
  onCallFlag?: boolean;
  onCallDetails?: string;
  defaultRate?: string;
  rateType?: "perHour" | "contracted" | string;
}

export interface ShiftTemplateResponse {
  templateId: string;
  templateName: string;
  specialty?: string | null;
  skillLevel?: string | null;
  supportLevel?: string | null;
  additionalFilesRequired?: string[];
  travelFlag?: number;
  travelType?: string | null;
  travelDetails?: string;
  fightsIncludedFlag?: number;
  accommodationFlag?: number;
  accommodationType?: string | null;
  accommodation?: string;
  startTime?: string;
  endTime?: string;
  description?: string;
  positionsRequired?: number;
  acceptCriteria?: AcceptCriterion[];
  onCallFlag?: boolean;
  onCallDetails?: string;
  defaultRate?: string;
  rateType?: string;
  createTime?: number;
}

export const createTemplate = async (data: ShiftTemplateRequest) => {
  const response = await axios.post("/v1/hospital/shift-templates", data);
  return response.data;
};

export const updateTemplate = async (
  data: Partial<ShiftTemplateRequest> & { templateId: string },
) => {
  const response = await axios.put("/v1/hospital/shift-templates", data);
  return response.data;
};

export const getTemplates = async () => {
  const response = await axios.get("/v1/hospital/shift-templates");
  return response.data;
};

/** Normalize GET /shift-templates body to a template array. */
export function parseTemplateListResponse(res: unknown): ShiftTemplateResponse[] {
  if (Array.isArray(res)) return res as ShiftTemplateResponse[];
  if (!res || typeof res !== "object") return [];
  const body = res as { code?: number; data?: unknown; message?: string };
  if (body.code != null && body.code !== 200 && body.code !== 0) return [];
  const data = body.data;
  return Array.isArray(data) ? (data as ShiftTemplateResponse[]) : [];
}

export function getTemplateListError(res: unknown): string | null {
  if (!res || typeof res !== "object") return "Could not load templates";
  const body = res as { code?: number; message?: string; data?: unknown };
  if (body.code === 200 || body.code === 0) return null;
  if (body.code == null && Array.isArray(body.data)) return null;
  return body.message || "Could not load templates";
}

export function formatTemplateDefaultRate(
  template: Pick<ShiftTemplateResponse, "defaultRate" | "rateType">,
): string | null {
  if (!template.defaultRate?.trim()) return null;
  const rate = template.defaultRate.trim();
  if (template.rateType === "contracted") return `$${rate}/day`;
  return `$${rate}/hr`;
}

export function formatTemplateRateTypeLabel(rateType?: string | null): string {
  const normalized = String(rateType || "").trim().toLowerCase();
  if (normalized === "contracted" || normalized === "perday") {
    return "Per day (contracted)";
  }
  if (normalized === "perhour" || normalized === "per_hour") {
    return "Per hour";
  }
  return rateType?.trim() || "Not specified";
}

export function formatTemplateShiftTime(
  template: Pick<ShiftTemplateResponse, "startTime" | "endTime">,
): string | null {
  const start = String(template.startTime || "").trim();
  const end = String(template.endTime || "").trim();
  if (start && end) return `${start} – ${end}`;
  if (start) return start;
  if (end) return end;
  return null;
}

export function formatTemplateFlag(flag?: number | boolean | null): boolean {
  if (typeof flag === "boolean") return flag;
  return Number(flag) === 1;
}

export function formatTemplateTimestamp(createTime?: number | null): string | null {
  if (!createTime) return null;
  const ms = createTime > 946684800000 ? createTime : createTime * 1000;
  try {
    return new Date(ms).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

export function formatTemplateTravelSummary(
  template: Pick<
    ShiftTemplateResponse,
    "travelFlag" | "travelType" | "travelDetails" | "fightsIncludedFlag"
  >,
): {
  included: boolean;
  type: string | null;
  details: string | null;
  flightsIncluded: boolean;
} {
  const included = formatTemplateFlag(template.travelFlag);
  return {
    included,
    type: included ? String(template.travelType || "").trim() || null : null,
    details: included
      ? String(template.travelDetails || "").trim() || null
      : null,
    flightsIncluded:
      included && formatTemplateFlag(template.fightsIncludedFlag),
  };
}

export function formatTemplateAccommodationSummary(
  template: Pick<
    ShiftTemplateResponse,
    "accommodationFlag" | "accommodationType" | "accommodation"
  >,
): {
  included: boolean;
  type: string | null;
  details: string | null;
} {
  const included = formatTemplateFlag(template.accommodationFlag);
  return {
    included,
    type: included
      ? String(template.accommodationType || "").trim() || null
      : null,
    details: included
      ? String(template.accommodation || "").trim() || null
      : null,
  };
}

export function normalizeTemplateDocuments(
  value: unknown,
): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

export function formatTemplateAcceptCriterionLabel(
  row: AcceptCriterion & { specialtyName?: string; minSkillLevelName?: string },
  specialtyMap: Record<string, string> = {},
  skillMap: Record<string, string> = {},
): string {
  const specialty =
    row.specialtyName ||
    specialtyMap[row.specialtyId] ||
    row.specialtyId ||
    "Specialty";
  const skill =
    row.minSkillLevelName ||
    skillMap[row.minSkillLevelId] ||
    row.minSkillLevelId ||
    "Skill level";
  const rate = formatAcceptCriterionRate(row);
  return rate ? `${specialty} • ${skill} • ${rate}` : `${specialty} • ${skill}`;
}

export const deleteTemplate = async (templateId: string) => {
  const response = await axios.delete(
    "/v1/hospital/shift-templates/" + templateId,
  );
  return response.data;
};
